import React, { useCallback, useEffect, useRef, useState } from 'react';
import ScSpinner from '../ScSpinner';
import LetterPlaceholderPalette from '../LetterPlaceholderPalette';
import LetterTemplateTextarea, { insertAtCursor } from '../LetterTemplateTextarea';
import { fetchLetterTemplates, saveLetterTemplate } from '../../studyConfigurationApi';
import '../LetterTemplatesTab.css';

const emptySection = () => ({
  headerTemplate: '',
  bodyTemplate: '',
  footerTemplate: '',
});

const LetterTemplatesTab = ({ projectCode }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [visitForm, setVisitForm] = useState(emptySection);
  const [followUpForm, setFollowUpForm] = useState(emptySection);
  const [savingKind, setSavingKind] = useState(null);
  const focusRef = useRef(null);

  const load = useCallback(async () => {
    if (!projectCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetchLetterTemplates(projectCode);
      const data = res.data || {};
      setVisitForm({
        headerTemplate: data.visit?.headerTemplate ?? data.Visit?.headerTemplate ?? '',
        bodyTemplate: data.visit?.bodyTemplate ?? data.Visit?.bodyTemplate ?? '',
        footerTemplate: data.visit?.footerTemplate ?? data.Visit?.footerTemplate ?? '',
      });
      setFollowUpForm({
        headerTemplate: data.followUp?.headerTemplate ?? data.FollowUp?.headerTemplate ?? '',
        bodyTemplate: data.followUp?.bodyTemplate ?? data.FollowUp?.bodyTemplate ?? '',
        footerTemplate: data.followUp?.footerTemplate ?? data.FollowUp?.footerTemplate ?? '',
      });
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load letter templates.');
    } finally {
      setLoading(false);
    }
  }, [projectCode]);

  useEffect(() => { load(); }, [load]);

  const handleInsertPlaceholder = (token) => {
    const el = focusRef.current;
    if (!el) return;
    const result = insertAtCursor(el, token);
    if (!result) return;
    const fieldId = el.id || '';
    const apply = (prev) => {
      if (fieldId.includes('visit-header')) return { ...prev, headerTemplate: result.next };
      if (fieldId.includes('visit-body')) return { ...prev, bodyTemplate: result.next };
      if (fieldId.includes('visit-footer')) return { ...prev, footerTemplate: result.next };
      if (fieldId.includes('followup-header')) return { ...prev, headerTemplate: result.next };
      if (fieldId.includes('followup-body')) return { ...prev, bodyTemplate: result.next };
      if (fieldId.includes('followup-footer')) return { ...prev, footerTemplate: result.next };
      return prev;
    };
    if (fieldId.includes('followup')) {
      setFollowUpForm(apply);
    } else {
      setVisitForm(apply);
    }
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(result.caret, result.caret);
    });
  };

  const saveSection = async (letterKind, form) => {
    setSavingKind(letterKind);
    setError(null);
    setSuccess(null);
    try {
      await saveLetterTemplate({
        projectCode,
        letterKind,
        headerTemplate: form.headerTemplate,
        bodyTemplate: form.bodyTemplate,
        footerTemplate: form.footerTemplate,
        isActive: true,
      });
      setSuccess(letterKind === 'followup' ? 'Follow-up letter template saved.' : 'Visit letter template saved.');
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed.');
    } finally {
      setSavingKind(null);
    }
  };

  if (loading) return <ScSpinner />;

  return (
    <div>
      <p className="letter-template-project-label">
        Letter Templates — Project: <strong>{projectCode}</strong>
      </p>
      {error && <div className="sc-alert sc-alert-error">{error}</div>}
      {success && <div className="sc-alert sc-alert-info">{success}</div>}

      <LetterPlaceholderPalette onInsert={handleInsertPlaceholder} />

      <section className="letter-template-section">
        <h3>Visit Letter</h3>
        <LetterTemplateTextarea
          id="visit-header"
          label="Header"
          value={visitForm.headerTemplate}
          onChange={(v) => setVisitForm((f) => ({ ...f, headerTemplate: v }))}
          onFocus={(e) => { focusRef.current = e.target; }}
          rows={10}
        />
        <LetterTemplateTextarea
          id="visit-body"
          label="Body"
          value={visitForm.bodyTemplate}
          onChange={(v) => setVisitForm((f) => ({ ...f, bodyTemplate: v }))}
          onFocus={(e) => { focusRef.current = e.target; }}
          rows={8}
        />
        <LetterTemplateTextarea
          id="visit-footer"
          label="Footer"
          value={visitForm.footerTemplate}
          onChange={(v) => setVisitForm((f) => ({ ...f, footerTemplate: v }))}
          onFocus={(e) => { focusRef.current = e.target; }}
          rows={5}
        />
        <div className="letter-template-save-row">
          <button
            type="button"
            className="sc-btn sc-btn-primary"
            disabled={savingKind === 'visit'}
            onClick={() => saveSection('visit', visitForm)}
          >
            {savingKind === 'visit' ? 'Saving…' : 'Save Visit Letter Template'}
          </button>
        </div>
      </section>

      <section className="letter-template-section">
        <h3>Follow-Up Letter</h3>
        <LetterTemplateTextarea
          id="followup-header"
          label="Header"
          value={followUpForm.headerTemplate}
          onChange={(v) => setFollowUpForm((f) => ({ ...f, headerTemplate: v }))}
          onFocus={(e) => { focusRef.current = e.target; }}
          rows={10}
        />
        <LetterTemplateTextarea
          id="followup-body"
          label="Body"
          value={followUpForm.bodyTemplate}
          onChange={(v) => setFollowUpForm((f) => ({ ...f, bodyTemplate: v }))}
          onFocus={(e) => { focusRef.current = e.target; }}
          rows={6}
        />
        <LetterTemplateTextarea
          id="followup-footer"
          label="Footer"
          value={followUpForm.footerTemplate}
          onChange={(v) => setFollowUpForm((f) => ({ ...f, footerTemplate: v }))}
          onFocus={(e) => { focusRef.current = e.target; }}
          rows={5}
        />
        <div className="letter-template-save-row">
          <button
            type="button"
            className="sc-btn sc-btn-primary"
            disabled={savingKind === 'followup'}
            onClick={() => saveSection('followup', followUpForm)}
          >
            {savingKind === 'followup' ? 'Saving…' : 'Save Follow-Up Letter Template'}
          </button>
        </div>
      </section>
    </div>
  );
};

export default LetterTemplatesTab;
